import { supabase } from "@/integrations/supabase/client";

export interface VoiceCallRow {
  id: string;
  customer_name?: string;
  customer_number?: string;
  phone_number?: string;
  duration_seconds?: number;
  total_cost?: number;
  call_status?: string;
  status?: string;
  call_type?: string;
  end_reason?: string;
  summary?: string;
  transcript?: string;
  recording_url?: string;
  started_at?: string;
  ended_at?: string;
  created_at: string;
  transfer_destination?: string;
  vapi_call_id: string;
  voice_agent_id: string;
  extracted_sentiment?: string;
  extracted_priority?: string;
  intent?: string;
  next_step?: string;
  tags?: string[];
  voice_agents?: {
    id: string;
    title: string;
    agent_name: string;
    organization_id: string;
  };
}

export interface VoiceAgent {
  id: string;
  agent_name: string;
  title: string;
  organization_id: string;
}

export const voiceCallsService = {
  /**
   * Fetch all voice calls for the user's organization
   */
  async getVoiceCalls(organizationId: string): Promise<{
    data: VoiceCallRow[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("voice_calls")
        .select(
          `
          *,
          voice_agents!inner(
            id,
            title,
            agent_name,
            organization_id
          )
        `,
        )
        .eq("voice_agents.organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return { data: data as VoiceCallRow[], error: null };
    } catch (error) {
      console.error("Failed to fetch voice calls:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Fetch all voice agents for the user's organization
   */
  async getVoiceAgents(organizationId: string): Promise<{
    data: VoiceAgent[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("voice_agents")
        .select("id, agent_name, title, organization_id")
        .eq("organization_id", organizationId)
        .eq("archived", false)
        .order("title", { ascending: true });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch voice agents:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update voice call status
   */
  async updateCallStatus(
    callId: string,
    status: string,
  ): Promise<{
    data: VoiceCallRow | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("voice_calls")
        .update({ status })
        .eq("id", callId)
        .select()
        .single();

      if (error) throw error;

      return { data: data as VoiceCallRow, error: null };
    } catch (error) {
      console.error("Failed to update call status:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get a single voice call by ID
   */
  async getVoiceCall(callId: string): Promise<{
    data: VoiceCallRow | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("voice_calls")
        .select(
          `
          *,
          voice_agents(
            id,
            title,
            agent_name,
            organization_id
          )
        `,
        )
        .eq("id", callId)
        .single();

      if (error) throw error;

      return { data: data as VoiceCallRow, error: null };
    } catch (error) {
      console.error("Failed to fetch voice call:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },
};
